import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SEOHead } from "../components/SEOHead";
import { BASE_URL } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { authFetch, getApiBase, getPathPrefix, resolveMediaUrl } from "../../utils/preview";
import "../styles/PublicCommon.css";
import "./NewsDetailPage.css";

// APIから取得するデータの型定義
interface AnnounceMedia {
  name: string;
  size: number;
  content_type: string;
  media_type: string;
  storage_path: string;
  display_order: number;
  caption: string;
  key_name?: string;
}

interface Announce {
  id: string;
  title: string;
  content: string;
  thumbnail_image: AnnounceMedia | null;
  gallery_images: AnnounceMedia[];
  videos: AnnounceMedia[];
  // 本文中に挿入する画像・動画。KeyName で `![](image:KEY)` / `![](video:KEY)` から参照される
  content_media?: AnnounceMedia[];
  display_priority: number;
  publish_date: string;
  is_published: boolean;
  create_date: string;
  update_date: string;
}

// 日付をフォーマットする関数
const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// メディアURLを生成（/static/ も R2 も透過的に扱う）
const getMediaUrl = (storagePath: string): string => resolveMediaUrl(storagePath);

// ギャラリー1枠分の表示モデル（画像/動画共通）
interface GalleryItem {
  url: string;
  caption: string;
  mediaType: "image" | "video";
  contentType?: string;
  poster?: string; // 動画用ポスター
}

export const NewsDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const pathPrefix = getPathPrefix();
  const [announce, setAnnounce] = useState<Announce | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 存在しない ID（API が 404）のときは検索結果に載せない。一時的な取得失敗では付けない
  const [notFound, setNotFound] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // APIからデータを取得
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await authFetch(`${getApiBase()}/announces/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            setNotFound(true);
            throw new Error("お知らせが見つかりませんでした");
          }
          throw new Error("データの取得に失敗しました");
        }
        const data = await response.json();
        setAnnounce(data.announce);
        setSelectedIndex(0);
        setError(null);
        setNotFound(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "エラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // content_media の keyName -> {storagePath, mediaType} マップ
  // 本文中の `image:KEY` / `video:KEY` を実 URL に変換するために使う
  const contentMediaMap = useMemo(() => {
    const map = new Map<string, { storagePath: string; mediaType: string }>();
    (announce?.content_media || []).forEach((m) => {
      const key = m.key_name || "";
      if (key && m.storage_path) {
        map.set(key, { storagePath: m.storage_path, mediaType: m.media_type || "image" });
      }
    });
    return map;
  }, [announce?.content_media]);

  // 本文中の `image:KEY` を実 URL に置換、`video:KEY` は `[VIDEO:KEY:URL]` の中間記法に変換。
  // 動画はあとで ReactMarkdown のカスタム <p> コンポーネントが <video> に描画する。
  const processedContent = useMemo(() => {
    let processed = announce?.content || "";

    processed = processed.replace(
      /!\[([^\]]*)\]\(image:([^)]+)\)/g,
      (match, alt, keyName) => {
        const media = contentMediaMap.get(keyName);
        return media ? `![${alt}](${getMediaUrl(media.storagePath)})` : match;
      }
    );

    processed = processed.replace(
      /!\[([^\]]*)\]\(video:([^)]+)\)/g,
      (match, _alt, keyName) => {
        const media = contentMediaMap.get(keyName);
        return media ? `\n\n[VIDEO:${keyName}:${getMediaUrl(media.storagePath)}]\n\n` : match;
      }
    );

    return processed;
  }, [announce?.content, contentMediaMap]);

  // ReactMarkdown のカスタム <p> 描画: `[VIDEO:KEY:URL]` を inline <video> に置換
  const markdownComponents = useMemo(
    () => ({
      p: ({ children, ...props }: any) => {
        const renderChild = (child: any, idx: number) => {
          if (typeof child !== "string") return child;
          const m = child.match(/^\[VIDEO:([^:]+):(.+)\]$/);
          if (!m) return child;
          const [, , url] = m;
          return (
            <video
              key={idx}
              controls
              preload="metadata"
              playsInline
              className="news-content-inline-video"
              src={url}
            >
              お使いのブラウザは動画再生に対応していません。
            </video>
          );
        };
        if (typeof children === "string") {
          const replaced = renderChild(children, 0);
          if (replaced !== children) return <>{replaced}</>;
          return <p {...props}>{children}</p>;
        }
        if (Array.isArray(children)) {
          return <p {...props}>{children.map(renderChild)}</p>;
        }
        return <p {...props}>{children}</p>;
      },
    }),
    []
  );

  // 本文中で動画として参照されたキー（"その他の動画" 重複表示を避けるため）
  const inlinedVideoKeys = useMemo(() => {
    const set = new Set<string>();
    const re = /!\[[^\]]*\]\(video:([^)]+)\)/g;
    const content = announce?.content || "";
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) set.add(m[1]);
    return set;
  }, [announce?.content]);

  // 全メディアリスト（動画 → サムネイル画像 → ギャラリー画像 の順）
  // 「ブランド動画を先頭に」要件のため、videos[] を先に並べる。
  // 本文中で `video:KEY` 参照済みのものは重複を避けるため除外する。
  const allMedia = useMemo<GalleryItem[]>(() => {
    if (!announce) return [];
    const items: GalleryItem[] = [];

    const posterUrl = announce.thumbnail_image?.storage_path
      ? getMediaUrl(announce.thumbnail_image.storage_path)
      : undefined;

    (announce.videos || []).forEach((v) => {
      if (!v.storage_path) return;
      if (v.key_name && inlinedVideoKeys.has(v.key_name)) return;
      items.push({
        url: getMediaUrl(v.storage_path),
        caption: v.caption || v.name,
        mediaType: "video",
        contentType: v.content_type || "video/mp4",
        poster: posterUrl,
      });
    });

    if (announce.thumbnail_image?.storage_path) {
      items.push({
        url: getMediaUrl(announce.thumbnail_image.storage_path),
        caption: announce.thumbnail_image.caption || "サムネイル",
        mediaType: "image",
      });
    }
    (announce.gallery_images || []).forEach((img) => {
      if (img.storage_path) {
        items.push({
          url: getMediaUrl(img.storage_path),
          caption: img.caption || img.name,
          mediaType: "image",
        });
      }
    });
    return items;
  }, [announce, inlinedVideoKeys]);

  // 互換のため allImages も残す（ライトボックス用に画像のみ）
  const allImages = useMemo(
    () => allMedia.filter((m) => m.mediaType === "image"),
    [allMedia],
  );

  // 構造化データを生成
  const structuredData = useMemo(() => {
    if (!announce) return undefined;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "@id": `${BASE_URL}/news/${id}`,
      headline: announce.title,
      datePublished: announce.publish_date,
      dateModified: announce.update_date || announce.publish_date,
      author: {
        "@type": "Organization",
        name: "都建設株式会社",
        url: BASE_URL,
      },
      publisher: {
        "@type": "Organization",
        name: "都建設株式会社",
        url: BASE_URL,
        logo: {
          "@type": "ImageObject",
          url: `${BASE_URL}/images/logo.png`,
        },
      },
      ...(announce.thumbnail_image?.storage_path && {
        image: `${BASE_URL}/api/public/media/${announce.thumbnail_image.storage_path}`,
      }),
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${BASE_URL}/news/${id}`,
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ホーム",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "お知らせ",
          item: `${BASE_URL}/news`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: announce.title,
          item: `${BASE_URL}/news/${id}`,
        },
      ],
    };

    return [articleSchema, breadcrumbSchema];
  }, [announce, id]);

  // OGP画像
  const ogImage = announce?.thumbnail_image?.storage_path
    ? `${BASE_URL}/api/public/media/${announce.thumbnail_image.storage_path}`
    : undefined;

  // 抜粋テキスト生成
  const excerpt = announce?.content
    ? announce.content.replace(/[#*`\[\]()]/g, "").substring(0, 150)
    : "";

  return (
    <>
      <SEOHead
        title={announce ? `${announce.title} | お知らせ` : "お知らせ詳細"}
        description={
          announce
            ? `${excerpt}...`
            : "都建設株式会社からのお知らせです。"
        }
        canonicalUrl={`/news/${id}`}
        keywords="都建設,お知らせ,ニュース,愛媛県,西予市,建設会社"
        ogType="article"
        ogImage={ogImage}
        publishedTime={announce?.publish_date}
        structuredData={structuredData}
        noIndex={notFound}
      />

      <div className="public-page news-detail-page">
        <PublicHeader activePage="news" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* パンくずリスト */}
          <nav className="breadcrumb" aria-label="パンくずリスト">
            <div className="container">
              <Link to="/">ホーム</Link>
              <span className="breadcrumb-separator">/</span>
              <Link to={`${pathPrefix}/news`}>お知らせ</Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">
                {loading ? "読み込み中..." : announce?.title || "詳細"}
              </span>
            </div>
          </nav>

          {/* コンテンツ */}
          <article className="news-detail-section">
            <div className="container">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>読み込み中...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <div className="error-icon">!</div>
                  <p>{error}</p>
                  <Link to={`${pathPrefix}/news`} className="back-link">
                    お知らせ一覧に戻る
                  </Link>
                </div>
              ) : announce ? (
                <div className="news-detail-content">
                  {/* ヘッダー部分 */}
                  <header className="news-detail-header">
                    <time className="news-detail-date" dateTime={announce.publish_date}>
                      {formatDate(announce.publish_date)}
                    </time>
                    <h1 className="news-detail-title">{announce.title}</h1>
                  </header>

                  {/* メイン画像/動画エリア（動画はサムネイルから選択可能、画像はクリックで拡大） */}
                  {allMedia.length > 0 && (() => {
                    const selected = allMedia[Math.min(selectedIndex, allMedia.length - 1)];
                    return (
                      <div className="news-image-gallery">
                        {selected.mediaType === "video" ? (
                          <div className="news-main-image news-main-video">
                            <video
                              key={selected.url}
                              src={selected.url}
                              controls
                              preload="metadata"
                              playsInline
                              poster={selected.poster}
                            >
                              <source src={selected.url} type={selected.contentType || "video/mp4"} />
                              お使いのブラウザは動画再生に対応していません。
                            </video>
                            {selected.caption && (
                              <p className="video-caption">{selected.caption}</p>
                            )}
                          </div>
                        ) : (
                          <div
                            className="news-main-image"
                            onClick={() => setLightboxOpen(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && setLightboxOpen(true)}
                          >
                            <img src={selected.url} alt={announce.title} />
                            <div className="image-zoom-hint">
                              <span>クリックで拡大</span>
                            </div>
                          </div>
                        )}

                        {allMedia.length > 1 && (
                          <div className="news-thumbnail-list">
                            {allMedia.map((m, index) => (
                              <button
                                key={index}
                                className={`news-thumbnail ${selectedIndex === index ? "active" : ""} ${m.mediaType === "video" ? "is-video" : ""}`}
                                onClick={() => setSelectedIndex(index)}
                                aria-label={`${m.mediaType === "video" ? "動画" : "画像"} ${index + 1}件目を表示`}
                              >
                                <img
                                  src={m.mediaType === "video" ? (m.poster || "/static/miyako-logo-small.png") : m.url}
                                  alt={m.caption}
                                />
                                {m.mediaType === "video" && (
                                  <span className="news-thumbnail-play" aria-hidden="true">▶</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* 本文（image:/video: の参照を実体に展開し、動画は <video> でインライン描画） */}
                  <div className="news-detail-body markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {processedContent}
                    </ReactMarkdown>
                  </div>

                  {/* 動画は上部のギャラリーに統合済み（旧「関連動画」セクションは廃止） */}

                  {/* 戻るボタン */}
                  <div className="news-detail-actions">
                    <Link to={`${pathPrefix}/news`} className="back-button">
                      <span className="back-arrow">&larr;</span>
                      お知らせ一覧に戻る
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </article>

          {/* CTAセクション */}
          <section className="cta-section">
            <div className="container">
              <h2 className="cta-title">お見積もり・ご相談は無料です</h2>
              <p className="cta-text">
                お気軽にお問い合わせください。
                <br />
                お客様のご要望に合わせた最適なプランをご提案いたします。
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="cta-button primary">
                  お問い合わせフォーム
                </Link>
                <a href="tel:0894-62-4511" className="cta-button secondary">
                  <span>電話で問い合わせ</span>
                  <span className="phone-number">0894-62-4511</span>
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* ライトボックス */}
        {lightboxOpen && (
          <div
            className="lightbox-overlay"
            onClick={() => setLightboxOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="画像拡大表示"
          >
            <button
              className="lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="閉じる"
            >
              &times;
            </button>
            <img
              src={allMedia[Math.min(selectedIndex, allMedia.length - 1)]?.url}
              alt={announce?.title}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small.png"
                srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>
              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <Link to="/">ホーム</Link>
              <Link to="/about">会社概要</Link>
              <Link to="/services">業務内容</Link>
              <Link to="/works">施工実績</Link>
              <Link to="/recruit">採用情報</Link>
              <Link to="/contact">お問い合わせ</Link>
            </nav>

            <p className="footer-copyright">
              <small>&copy;2026 都建設株式会社</small>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default NewsDetailPage;
